import React from 'react'

import { usePortal } from '../../../hooks'
import { SnackbarProps } from '../Snackbar'
import { SnackbarVariant } from '../types'
import { Container, StyledSnackbar } from './styles'

// Space between snackbars in px
const SNACKBAR_SPACING = 16
const DEFAULT_SNACKBAR_DURATION = 1000 * 5

export interface SnackbarOption {
  variant?: SnackbarVariant
  message: SnackbarProps['children']
  extra?: SnackbarProps['extra']
  action?: SnackbarProps['action']
  duration?: number
}

interface SnackbarContextValue {
  openSnackbar: (option: SnackbarOption) => void
}

const uninitialized = () => {
  throw new Error('SnackbarProvider not initialized')
}

const SnackbarContext = React.createContext<SnackbarContextValue>({
  openSnackbar: uninitialized,
})

export interface SnackbarProviderProps {
  /**
   * Disable portal
   */
  disablePortal?: boolean

  /**
   * Children
   */
  children?: React.ReactNode
}

export const SnackbarProvider = ({
  disablePortal,
  children,
}: SnackbarProviderProps) => {
  const [{ snackbars }, setSnackbarState] = React.useState<{
    internalId: number
    snackbars: (SnackbarOption & { id: number })[]
  }>({ internalId: 1, snackbars: [] })

  const [heightMap, setHeightMap] = React.useState<{ [key: number]: number }>(
    {},
  )

  const { Portal } = usePortal()

  const openSnackbar = React.useCallback((option: SnackbarOption) => {
    setSnackbarState(({ snackbars, internalId }) => ({
      internalId: internalId + 1,
      snackbars: [{ ...option, id: internalId }, ...snackbars],
    }))
  }, [])

  const handleClose = React.useCallback((id?: number) => {
    setSnackbarState((prev) => ({
      ...prev,
      snackbars: prev.snackbars.filter((t) => t.id !== id),
    }))
  }, [])

  const setHeight = React.useCallback(
    ({ id, height }: { id: number; height: number }) => {
      setHeightMap((v) => ({ ...v, [id]: height }))
    },
    [],
  )

  const getDistance = React.useCallback(
    (id: number): number => {
      const targetIndex = snackbars.findIndex((t) => t.id === id)
      if (targetIndex === -1) return 0

      const prevSnackbars = snackbars.slice(0, targetIndex)

      return prevSnackbars.reduce<number>(
        (acc, t) => acc + (heightMap[t.id] ?? 0),
        prevSnackbars.length * SNACKBAR_SPACING,
      )
    },
    [snackbars, heightMap],
  )

  const value = React.useMemo(() => ({ openSnackbar }), [openSnackbar])

  const content = (
    <Container>
      {snackbars.map(({ id, message, duration, ...rest }) => (
        <StyledSnackbar
          key={id}
          id={id}
          distance={getDistance(id)}
          duration={duration ?? DEFAULT_SNACKBAR_DURATION}
          onClose={handleClose}
          measure={setHeight}
          {...rest}
        >
          {message}
        </StyledSnackbar>
      ))}
    </Container>
  )

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {!disablePortal ? <Portal>{content}</Portal> : content}
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = () => {
  return React.useContext(SnackbarContext)
}
